import {
  IOfferSchema,
  IHouseBTIDataSchema,
  IBuildingSchema,
  IUserSchema,
} from '@cian/api-models/offer-card/v1/get-offer-data';
import { isNil } from 'ramda';
import * as React from 'react';

import {
  getHouseMaterialType,
  getOverlapType,
  getHeatingType,
  getGasSupplyType,
  getParkingType,
  getHasLabel,
} from './helpers';
import * as styles from './index.css';
import { getOfferType, isUrban } from '../../utils/offer_type';
import { SubscriptionButton } from '../subscription_address/experiments/subscription_button/subscription_button';
import { SubscriptionPopup } from '../subscription_address/subscription_popup';
import { ICreateSubscriptionRequest } from '../../repositories/monolith-python/v1/create-subscription';

import { Item } from './Item';
import { Elevators } from '../../containers/BtiHouseData/Elevators';

export interface IBtiHouseDataStoreProps {
  houseData: IHouseBTIDataSchema | null;
  jkName: string;
  roomCount: number | null;
  offer: IOfferSchema;

  isLayoutSubscribeAddressExperiment: boolean;
  isOpen: boolean;
  isLoad: boolean;
  isSubscribe: boolean;
  globalError?: string;
  user: IUserSchema | null;
  pushSubscribed: boolean;
}

export interface IBtiHouseDataStoreDispatchProps {
  setVisible(isOpen: boolean): void;
  fetchCreateSubscription(parameters: ICreateSubscriptionRequest): Promise<void>;
}

export type TBtiHouseDataProps = IBtiHouseDataStoreProps & IBtiHouseDataStoreDispatchProps;

export class BtiHouseData extends React.Component<TBtiHouseDataProps, {}> {
  public render() {
    const {
      houseData,
      offer,
      isLayoutSubscribeAddressExperiment,
      setVisible,
      fetchCreateSubscription,
      isOpen,
      isLoad,
      isSubscribe,
      globalError,
      user,
      pushSubscribed,
    } = this.props;
    const { building } = offer;

    const offerType = getOfferType(offer);
    const isOfferUrban = isUrban(offerType);

    // bti
    const entrances = houseData && houseData.entrances;
    const isEmergency = houseData && !isNil(houseData.isEmergency) ? houseData.isEmergency : null;
    const seriesName = houseData && houseData.seriesName;
    const yearRelease = (houseData && houseData.yearRelease) || null;
    const houseOverlapType = houseData && houseData.houseOverlapType && getOverlapType(houseData.houseOverlapType);
    const houseHeatSupplyType =
      houseData && houseData.houseHeatSupplyType && getHeatingType(houseData.houseHeatSupplyType);
    const houseGasSupplyType =
      houseData && houseData.houseGasSupplyType && getGasSupplyType(houseData.houseGasSupplyType);

    // user provided
    const parkingType = building && building.parking && building.parking.type && getParkingType(building.parking.type);
    const garbageChute = getHasLabel(building && building.hasGarbageChute);
    const serviceElevatorsRaw = building && building.cargoLiftsCount;
    const elevatorsRaw = building && building.passengerLiftsCount;
    const elevators = elevatorsRaw || serviceElevatorsRaw;
    const hasGas = getHasLabel(offer.hasGas);

    // AI
    let houseMaterialType = findHouseMaterialType(houseData, building);

    const isBtiDataUnavailable =
      !entrances &&
      isEmergency === null &&
      !seriesName &&
      !yearRelease &&
      !houseMaterialType &&
      !houseOverlapType &&
      !houseHeatSupplyType &&
      !houseGasSupplyType;

    const isUserProvidedDataUnavailable = !parkingType && !elevators && garbageChute === null && hasGas === null;

    if (isUserProvidedDataUnavailable && isBtiDataUnavailable) {
      return null;
    }

    return (
      <div className={styles['container']}>
        <div className={styles['title']}>
          <span>{this.props.jkName ? `ЖК \u00ab${this.props.jkName}\u00bb` : 'О доме'}</span>
          {!isBtiDataUnavailable && <span className={styles['note']}>По данным Циан</span>}
        </div>
        {isLayoutSubscribeAddressExperiment && (
          <>
            <SubscriptionButton setVisible={setVisible} />
            <SubscriptionPopup
              fetchCreateSubscription={fetchCreateSubscription}
              offer={offer}
              isOpen={isOpen}
              isLoad={isLoad}
              isSubscribe={isSubscribe}
              globalError={globalError}
              onClose={() => setVisible(false)}
              isAuthenticated={Boolean(user && user.isAuthenticated)}
              user={user}
              pushSubscribed={pushSubscribed}
            />
          </>
        )}
        <div className={styles['content']}>
          <div className={styles['column']}>
            {yearRelease && <Item name="Год постройки" value={yearRelease} />}
            {seriesName && <Item name="Строительная серия" value={seriesName} />}
            {houseMaterialType && <Item name="Тип дома" value={houseMaterialType} />}
            {houseOverlapType && <Item name="Тип перекрытий" value={houseOverlapType} />}
            {entrances && <Item name="Подъезды" value={entrances} />}
            {isOfferUrban && <Elevators />}
            {houseHeatSupplyType && <Item name="Отопление" value={houseHeatSupplyType} />}
            {isEmergency !== null && <Item name="Аварийность" value={isEmergency ? 'Да' : 'Нет'} />}
            {parkingType && <Item name="Парковка" value={parkingType} />}
            {garbageChute !== null && <Item name="Мусоропровод" value={garbageChute} />}
            {(houseGasSupplyType || hasGas) && (
              <Item name="Газоснабжение" value={(houseGasSupplyType || hasGas) as string} />
            )}
          </div>
        </div>
      </div>
    );
  }
}

/* istanbul ignore next */
function findHouseMaterialType(houseData: IHouseBTIDataSchema | null, building: IBuildingSchema | null): string | null {
  if (houseData && houseData.houseMaterialType) {
    const houseMaterialType = houseData.houseMaterialType;

    if (houseMaterialType !== 'none' && houseMaterialType !== 'custom') {
      return getHouseMaterialType(houseMaterialType);
    }
  } else if (building && building.materialType) {
    const materialType = building.materialType as string;

    if (materialType !== 'none' && materialType !== 'custom') {
      return getHouseMaterialType(building.materialType);
    }
  }

  return null;
}
